import { useState, useEffect } from 'react'
import { get, post, patch, del } from '../lib/api'
import Drawer from './Drawer'

const PRIORITY = {
  high:   { label: 'High',   color: '#EF4444', bg: '#FEF2F2' },
  medium: { label: 'Medium', color: '#D97706', bg: '#FFFBEB' },
  low:    { label: 'Low',    color: '#059669', bg: '#ECFDF5' },
}
const STATUS = {
  assigned:    { label: 'Assigned',    color: '#4F46E5', bg: '#EEF2FF' },
  accepted:    { label: 'Accepted',    color: '#0EA5E9', bg: '#E0F2FE' },
  in_progress: { label: 'In Progress', color: '#D97706', bg: '#FFFBEB' },
  completed:   { label: 'Completed',   color: '#059669', bg: '#ECFDF5' },
  declined:    { label: 'Declined',    color: '#EF4444', bg: '#FEF2F2' },
}

export default function Jobs({ t, isEmployer }) {
  const [jobs, setJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [empMap, setEmpMap] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', employee_id: '', attachment_data: '', attachment_name: '' })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [viewJob, setViewJob] = useState(null)
  const [noteDraft, setNoteDraft] = useState({})

  useEffect(() => { fetchAll() }, [])

  async function handleAttach(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 300 * 1024) { setMsg('❌ Attachment must be under 300 KB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) { setMsg('❌ Only JPG, PNG, WebP or PDF'); return }
    const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file) })
    setForm((f) => ({ ...f, attachment_data: dataUrl, attachment_name: file.name }))
    setMsg('')
  }

  async function fetchAll() {
    try {
      const r = await get('/jobs')
      setJobs(r.jobs || [])
      if (isEmployer) {
        const u = await get('/users')
        const emps = (u.users || []).filter((x) => x.role === 'employee')
        setEmployees(emps)
        setEmpMap(Object.fromEntries((u.users || []).map((x) => [x.id, x])))
      }
    } catch (e) { console.error(e.message) }
  }

  async function assign(e) {
    e.preventDefault()
    if (!form.title.trim() || !form.employee_id) { setMsg('❌ Title and employee are required'); return }
    setSaving(true); setMsg('')
    try {
      await post('/jobs', form)
      setForm({ title: '', description: '', priority: 'medium', due_date: '', employee_id: '', attachment_data: '', attachment_name: '' })
      setShowForm(false)
      fetchAll()
    } catch (err) { setMsg(`❌ ${err.message}`) }
    setSaving(false)
  }

  async function setStatus(job, status) {
    const note = noteDraft[job.id]
    try {
      await patch(`/jobs/${job.id}`, { status, ...(note && note.trim() ? { employee_note: note.trim() } : {}) })
      setNoteDraft((d) => ({ ...d, [job.id]: '' }))
      fetchAll()
    } catch (err) { alert(err.message) }
  }
  async function saveNote(job) {
    const note = (noteDraft[job.id] || '').trim()
    if (!note) return
    try { await patch(`/jobs/${job.id}`, { employee_note: note }); setNoteDraft((d) => ({ ...d, [job.id]: '' })); fetchAll() } catch (err) { alert(err.message) }
  }
  async function remove(id) {
    if (!window.confirm('Delete this job?')) return
    try { await del(`/jobs/${id}`); fetchAll() } catch (err) { alert(err.message) }
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }
  const card = { background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, boxShadow: t.shadow }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>{isEmployer ? 'Job Requests' : 'Assigned Jobs'}</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>{isEmployer ? 'Assign tasks to your team' : 'Tasks assigned to you'}</p>
        </div>
        {isEmployer && (
          <button onClick={() => setShowForm((s) => !s)} style={{ padding: '10px 18px', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
            {showForm ? 'Cancel' : '+ Assign job'}
          </button>
        )}
      </div>

      {isEmployer && showForm && (
        <form onSubmit={assign} style={{ ...card, padding: '1.5rem', marginBottom: 16 }}>
          <div style={{ marginBottom: '1rem' }}><label style={LS}>Job title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Prepare Q3 report" style={IS} /></div>
          <div style={{ marginBottom: '1rem' }}><label style={LS}>Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Details / expectations…" style={{ ...IS, resize: 'vertical' }} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: '1rem' }}>
            <div><label style={LS}>Assign to</label><select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} style={IS}><option value="">Select…</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}</select></div>
            <div><label style={LS}>Priority</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={IS}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            <div><label style={LS}>Due date</label><input type="date" min={new Date().toISOString().split('T')[0]} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} style={IS} /></div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={LS}>Attachment (image or PDF, max 300 KB)</label>
            <input type="file" accept="image/*,application/pdf" onChange={handleAttach} style={{ fontSize: 13, color: t.text }} />
            {form.attachment_name && <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>✓ {form.attachment_name}</div>}
          </div>
          {msg && <div style={{ fontSize: 13, color: '#EF4444', marginBottom: 12 }}>{msg}</div>}
          <button type="submit" disabled={saving} style={{ padding: '11px 22px', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>{saving ? 'Assigning…' : 'Assign job'}</button>
        </form>
      )}

      {jobs.length === 0 ? (
        <p style={{ color: t.textMuted }}>{isEmployer ? 'No jobs assigned yet.' : 'No jobs assigned to you yet.'}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {jobs.map((job) => {
            const pr = PRIORITY[job.priority] || PRIORITY.medium
            const st = STATUS[job.status] || STATUS.assigned
            return (
              <div key={job.id} style={{ ...card, padding: '1.1rem 1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{job.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: pr.bg, color: pr.color, padding: '2px 8px', borderRadius: 99 }}>{pr.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 99 }}>{st.label}</span>
                    </div>
                    {job.description && <div style={{ fontSize: 13, color: t.textSub, marginTop: 4, lineHeight: 1.5 }}>{job.description}</div>}
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span>{isEmployer && empMap[job.employee_id] && <>👤 {empMap[job.employee_id].full_name} · </>}{job.due_date ? `Due ${job.due_date}` : 'No due date'}</span>
                      {job.attachment_data && <a href={job.attachment_data} download={job.attachment_name || 'attachment'} style={{ color: '#4F46E5', fontWeight: 600 }}>📎 {job.attachment_name || 'attachment'}</a>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <button onClick={() => setViewJob(job)} className="eh-press" style={{ padding: '7px 14px', background: '#4F46E514', color: '#4F46E5', border: '1px solid #4F46E530', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>Open →</button>
                    {isEmployer && <button onClick={() => remove(job.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: t.textMuted }}>🗑</button>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Job details slide-over */}
      <Drawer open={!!viewJob} onClose={() => setViewJob(null)} t={t}
        title={viewJob?.title}
        subtitle={viewJob ? `${(PRIORITY[viewJob.priority] || PRIORITY.medium).label} priority` : ''}
        footer={viewJob && !isEmployer && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {viewJob.status === 'assigned' && <>
              <DrawerBtn onClick={() => { setStatus(viewJob, 'accepted'); setViewJob(null) }} color="#0EA5E9" fill>Accept job</DrawerBtn>
              <DrawerBtn onClick={() => { setStatus(viewJob, 'declined'); setViewJob(null) }} color="#EF4444">Decline</DrawerBtn>
            </>}
            {viewJob.status === 'accepted' && <DrawerBtn onClick={() => { setStatus(viewJob, 'in_progress'); setViewJob(null) }} color="#D97706" fill>Start working</DrawerBtn>}
            {viewJob.status === 'in_progress' && <DrawerBtn onClick={() => { setStatus(viewJob, 'completed'); setViewJob(null) }} color="#059669" fill>Mark done</DrawerBtn>}
          </div>
        )}>
        {viewJob && (() => {
          const st = STATUS[viewJob.status] || STATUS.assigned
          return (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, background: st.bg, color: st.color, padding: '4px 12px', borderRadius: 99 }}>{st.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, padding: '4px 0' }}>{viewJob.due_date ? `Due ${viewJob.due_date}` : 'No due date'}</span>
              </div>

              {isEmployer && empMap[viewJob.employee_id] && (
                <DetailBlock t={t} label="Assigned to" value={empMap[viewJob.employee_id].full_name} />
              )}

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Details from your manager</div>
                <div style={{ fontSize: 14, color: t.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{viewJob.description || '—'}</div>
              </div>

              {viewJob.attachment_data && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Attachment</div>
                  <a href={viewJob.attachment_data} download={viewJob.attachment_name || 'attachment'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#4F46E5', background: '#4F46E510', border: '1px solid #4F46E530', borderRadius: 8, padding: '8px 14px', textDecoration: 'none' }}>📎 {viewJob.attachment_name || 'Download attachment'}</a>
                </div>
              )}

              {viewJob.employee_note && (
                <div style={{ marginBottom: 16, background: t.input, borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 3 }}>{isEmployer ? "Employee's note" : 'Your last note'}</div>
                  <div style={{ fontSize: 14, color: t.text }}>{viewJob.employee_note}</div>
                </div>
              )}

              {!isEmployer && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Add a progress note</div>
                  <textarea value={noteDraft[viewJob.id] || ''} onChange={(e) => setNoteDraft((d) => ({ ...d, [viewJob.id]: e.target.value }))} rows={3} placeholder="Update your manager on progress…"
                    style={{ width: '100%', padding: '10px 12px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter,system-ui', boxSizing: 'border-box', resize: 'vertical' }} />
                  <button onClick={() => saveNote(viewJob)} style={{ marginTop: 8, padding: '8px 16px', background: '#4F46E514', color: '#4F46E5', border: '1px solid #4F46E530', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Send note</button>
                </div>
              )}
            </div>
          )
        })()}
      </Drawer>
    </div>
  )
}

function DetailBlock({ t, label, value }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: t.text, fontWeight: 600 }}>{value}</div>
    </div>
  )
}

function DrawerBtn({ onClick, color, fill, children }) {
  return (
    <button onClick={onClick} style={{ flex: fill ? 1 : 'none', padding: '11px 18px', background: fill ? color : `${color}14`, color: fill ? '#fff' : color, border: `1px solid ${fill ? color : color + '40'}`, borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>{children}</button>
  )
}

function ActBtn({ onClick, color, children }) {
  return (
    <button onClick={onClick} style={{ padding: '6px 12px', background: `${color}14`, color, border: `1px solid ${color}40`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>{children}</button>
  )
}
